import { getCart, updateCartItem, removeCartItem, clearCartApi, syncCart } from '../services/cartService';

const CURRENT_CART_VERSION = '2.0';
const GLOBAL_CART_KEY = 'persistentCart';
const STORE_CART_PREFIX = 'persistentCart_';
const LAST_STORE_KEY = 'lastStoreId';

// Each store keeps its own cart. Without a storeId we fall back to the legacy
// global key so existing callers/behaviour stay intact.
export const getCartKey = (storeId) => (storeId ? `${STORE_CART_PREFIX}${storeId}` : GLOBAL_CART_KEY);

export const loadCart = async (storeId) => {
  try {
    const result = await getCart(storeId);
    if (result.status === 'success' && Array.isArray(result.cart) && result.cart.length > 0) {
      return result.cart;
    }
    const savedCart = localStorage.getItem(getCartKey(storeId));
    if (!savedCart) return [];
    const cartData = JSON.parse(savedCart).items;
    return Array.isArray(cartData) ? cartData : [];
  } catch (error) {
    console.error('Error loading cart:', error);
    return [];
  }
};

export const saveCart = async (cart, storeId) => {
  try {
    if (!Array.isArray(cart)) return;
    const cartWithMetadata = {
      items: cart,
      version: CURRENT_CART_VERSION,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(getCartKey(storeId), JSON.stringify(cartWithMetadata));
    if (storeId) {
      localStorage.setItem(LAST_STORE_KEY, String(storeId));
    }
    await syncCart(cart, storeId);
  } catch (error) {
    console.error('Error saving cart:', error);
  }
};

export const mergeCarts = (cart1, cart2) => {
  const merged = [...cart1];
  cart2.forEach(item2 => {
    const existingIndex = merged.findIndex(item1 => 
      item1.id === item2.id && 
      JSON.stringify(item1.variation) === JSON.stringify(item2.variation)
    );
    if (existingIndex > -1) {
      merged[existingIndex].quantity += item2.quantity;
      merged[existingIndex].updatedAt = new Date().toISOString();
    } else {
      merged.push({ ...item2, addedAt: item2.addedAt || new Date().toISOString() });
    }
  });
  return merged;
};

export const clearCart = (storeId) => {
  try {
    localStorage.removeItem(getCartKey(storeId));
    clearCartApi(storeId);
  } catch (error) {
    console.error('Error clearing cart:', error);
  }
};

export const getCartItemCount = async (storeId) => {
  const cart = await loadCart(storeId);
  return cart.reduce((count, item) => count + item.quantity, 0);
};

export const getCartTotal = async (storeId) => {
  const cart = await loadCart(storeId);
  return cart.reduce((total, item) => total + ((item.finalPrice || item.per_unit_cost) * item.quantity), 0);
};

export const setupCartSync = (storeId, callback) => {
  const key = getCartKey(storeId);
  const handleStorageChange = (e) => {
    if (e.key === key) {
      callback?.();
    }
  };
  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
};
